// A marcação de texto do conteúdo editável — a linguagem inteira, em um arquivo.
//
// Puro, com prova em `app/tests/texto-rico.mjs`. Serve dois lados que não podem
// discordar: o conversor que traz o conteúdo do Bubble (`etl/`) e o renderizador
// que o aluno lê (`componentes/TextoRico.tsx`).
//
// ---------------------------------------------------------------------------
// Por que uma marcação, e não HTML no banco
// ---------------------------------------------------------------------------
// O caminho óbvio seria guardar HTML e jogar num `dangerouslySetInnerHTML`. Não:
// isso põe a maior superfície de XSS do hub numa coluna de texto, e o projeto já
// decidiu o contrário em dois lugares — `profiles.redes` guarda o HANDLE e não a
// URL, e `avatar_url` só aceita caminho dentro da pasta do próprio aluno, tudo
// para que um `javascript:` não chegue a um `href`.
//
// Aqui a saída são NÓS, e o renderizador monta elementos React. Não existe
// caminho de string para o DOM, então não existe injeção — nem se o texto vier de
// uma coluna que alguém editou à mão no banco.
//
// ---------------------------------------------------------------------------
// Os delimitadores foram MEDIDOS, não escolhidos por gosto
// ---------------------------------------------------------------------------
// Nos 215 mil caracteres do IR Pro no Bubble (19/08/2026): `**` aparece 0 vezes,
// `__` 0 vezes, `==` 0 vezes. É por isso que estes três são seguros como
// alternância. Itálico NÃO existe na marcação: o Bubble tem zero `[i]` no IR Pro,
// e o candidato natural (`//`) colide com `https://` — inventar uma marca que não
// é usada e é ambígua é criar defeito para o futuro.
//
// ---------------------------------------------------------------------------
// Link com URL inválida vira TEXTO, e é isso que dispensa escapar `[`
// ---------------------------------------------------------------------------
// `[texto](url)` só é link se a `url` passar por `urlSegura`. Um `](` literal no
// meio de uma frase (5 ocorrências no conteúdo real) não forma URL válida, então
// sai impresso como estava. Sem essa regra, cada `[` do texto precisaria de
// escape, e escape esquecido é conteúdo comido em silêncio.

/**
 * Um trecho de texto com as marcas que valem nele.
 *
 * UM tipo com marcas acumuláveis, e não uma união de tipos, porque o
 * aninhamento é o caso comum: `[b][u]rendimentos[/u][/b]` e
 * `[url=…][u]B3[/u][/url]` aparecem 192 vezes no conteúdo real. Uma união
 * obrigaria a escolher qual marca sobrevive.
 */
export type No = {
  texto: string;
  negrito?: boolean;
  sublinhado?: boolean;
  marcado?: boolean;
  /** Presente = é link. Já validada por `urlSegura`. */
  url?: string;
};

/** As três marcas de alternância. A ordem importa: `**` antes de `*`. */
const MARCAS = [
  { d: "**", chave: "negrito" },
  { d: "__", chave: "sublinhado" },
  { d: "==", chave: "marcado" },
] as const;

/**
 * A URL pode virar `href`?
 *
 * Aceita http e https, e mais nada. `http` está na lista porque 10 dos 43 links
 * reais do conteúdo apontam para `http://normas.receita.fazenda.gov.br` — reescrever
 * para https seria adivinhar que aquele host atende em TLS, e link quebrado é pior
 * que link sem TLS. O que a lista recusa é o que importa: `javascript:`, `data:`,
 * `vbscript:` e qualquer outro esquema.
 *
 * Devolve a URL normalizada, ou nulo — e nulo faz o trecho sair como texto comum.
 */
export function urlSegura(bruta: string): string | null {
  const limpa = bruta.trim();
  if (limpa === "") return null;
  let u: URL;
  try {
    u = new URL(limpa);
  } catch {
    return null;
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  return u.toString();
}

/**
 * Texto marcado → nós.
 *
 * Uma passada, com as marcas ativas num objeto que ALTERNA. É o que faz o
 * aninhamento sair de graça e em qualquer ordem: `**__x__**` e `__**x**__` dão o
 * mesmo resultado, e um fechamento fora de ordem não perde texto.
 */
export function analisar(entrada: string): No[] {
  const nos: No[] = [];
  const ativas = { negrito: false, sublinhado: false, marcado: false };
  let acumulado = "";
  let i = 0;

  const despejar = (extra?: Partial<No>) => {
    if (acumulado === "" && !extra) return;
    const no: No = { texto: acumulado };
    if (ativas.negrito) no.negrito = true;
    if (ativas.sublinhado) no.sublinhado = true;
    if (ativas.marcado) no.marcado = true;
    if (extra) Object.assign(no, extra);
    if (no.texto !== "") nos.push(no);
    acumulado = "";
  };

  while (i < entrada.length) {
    // Escape: `\` protege o caractere seguinte. Existe para o dia em que alguém
    // escrever `**` de propósito — hoje o conteúdo real não tem nenhum.
    if (entrada[i] === "\\" && i + 1 < entrada.length) {
      acumulado += entrada[i + 1];
      i += 2;
      continue;
    }

    const marca = MARCAS.find((m) => entrada.startsWith(m.d, i));
    if (marca) {
      despejar();
      ativas[marca.chave] = !ativas[marca.chave];
      i += marca.d.length;
      continue;
    }

    // `[texto](url)` — e só é link se a url passar.
    if (entrada[i] === "[") {
      const fim = entrada.indexOf("](", i);
      const fecha = fim === -1 ? -1 : entrada.indexOf(")", fim + 2);
      if (fim !== -1 && fecha !== -1) {
        const rotulo = entrada.slice(i + 1, fim);
        const url = urlSegura(entrada.slice(fim + 2, fecha));
        // Rótulo sem `[` dentro: o formato é raso de propósito, e um `[` ali
        // significa que o `[` de fora não era abertura de link.
        if (url && rotulo !== "" && !rotulo.includes("[")) {
          despejar();
          despejar({ texto: rotulo, url });
          i = fecha + 1;
          continue;
        }
      }
    }

    acumulado += entrada[i];
    i += 1;
  }

  despejar();
  return nos;
}

/** O texto sem marca nenhuma — para busca, resumo e `aria-label`. */
export function paraTextoSimples(entrada: string): string {
  return analisar(entrada)
    .map((n) => n.texto)
    .join("");
}

/**
 * Texto comum → texto marcado, sem virar marcação por acidente.
 *
 * Usado por quem TRAZ texto de fora (o conversor do Bubble, uma colagem futura).
 * Só escapa `\` e os três delimitadores: `[` não precisa, porque link com URL
 * inválida já sai como texto.
 */
export function escapar(bruto: string): string {
  return bruto
    .split("\\")
    .join("\\\\")
    .replace(/\*\*/g, "\\*\\*")
    .replace(/__/g, "\\_\\_")
    .replace(/==/g, "\\=\\=");
}

/** Envolve o trecho na marca, para o botão da barra de ferramentas do admin. */
export function envolver(texto: string, marca: "negrito" | "sublinhado" | "marcado"): string {
  const d = MARCAS.find((m) => m.chave === marca)!.d;
  return `${d}${texto}${d}`;
}
